import { useState, useRef } from "react";
import { Dog, Cat, AlertTriangle, ChevronRight, Clock, MapPin, Users, Heart, ChevronLeft, MapPinOff, Phone, Mail, BadgeCheck, ArrowUp } from "lucide-react";
import { trackEvent } from "@/hooks/useEventTracking";
import type { ShelterWithDistance } from "@/pages/Index";
import { isUrgentNeedsActive } from "@/types/shelter";
import { useIsTabletOrMobile } from "@/hooks/use-mobile";
import { formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ShareChoiceDialog } from "./ShareChoiceDialog";
import { EmptyState } from "./EmptyState";

interface ShelterListProps {
  shelters: ShelterWithDistance[];
  selectedShelterId: string | null;
  onShelterSelect: (shelter: ShelterWithDistance) => void;
  containerRef?: React.RefObject<HTMLDivElement>;
  onClearFilters?: () => void;
  hasActiveFilters?: boolean;
}

const ITEMS_PER_PAGE = 4;

// Check if shelter has a proper address (not just city name)
const hasExactLocation = (shelter: ShelterWithDistance): boolean => {
  if (!shelter.address) return false;
  const addressLower = shelter.address.toLowerCase().trim();
  const cityLower = shelter.city.toLowerCase().trim();
  if (addressLower === '' || addressLower === cityLower) return false;
  if (addressLower === `${cityLower}, polska` || addressLower === `${cityLower}, poland`) return false;
  return true;
};

export const ShelterList = ({ shelters, selectedShelterId, onShelterSelect, containerRef, onClearFilters, hasActiveFilters }: ShelterListProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const isTabletOrMobile = useIsTabletOrMobile();
  const listRef = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    containerRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const totalPages = Math.ceil(shelters.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  
  // On desktop show all shelters, on mobile/tablet use pagination
  const displayedShelters = isTabletOrMobile 
    ? shelters.slice(startIndex, startIndex + ITEMS_PER_PAGE)
    : shelters;

  // Reset to page 1 when shelters change (e.g., after filtering)
  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(1);
  }

  if (shelters.length === 0) {
    return (
      <EmptyState 
        type="no-results" 
        onClearFilters={onClearFilters}
        hasActiveFilters={hasActiveFilters}
      />
    );
  }

  const getLastUpdatedText = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: pl });
    } catch {
      return null;
    }
  };

  const formatDistance = (distance: number): string => {
    if (distance < 1) {
      return `${Math.round(distance * 1000)} m`;
    }
    return `${distance.toFixed(1)} km`;
  };

  return (
    <div className="space-y-3" ref={listRef}>
      {displayedShelters.map((shelter, index) => (
        <div
          key={shelter.id}
          data-shelter-id={shelter.id}
          role="button"
          tabIndex={0}
          onClick={() => {
            trackEvent("Otworzono schronisko", { shelter_id: shelter.id, shelter_name: shelter.name, shelter_city: shelter.city, source: "list" });
            onShelterSelect(shelter);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              trackEvent("Otworzono schronisko", { shelter_id: shelter.id, shelter_name: shelter.name, shelter_city: shelter.city, source: "list" });
              onShelterSelect(shelter);
            }
          }}
          className="shelter-card w-full text-left"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-2">
                <h3 className="font-display font-semibold text-card-foreground text-sm sm:text-base line-clamp-2 flex-1">
                  {shelter.name}
                </h3>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {shelter.organization_verified && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-semibold px-1.5 sm:px-2 py-0.5 rounded-full bg-primary/90 text-primary-foreground">
                            <BadgeCheck className="w-3 h-3" />
                            <span className="hidden sm:inline">Zweryfikowane</span>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[200px] text-center">
                          <p>Zweryfikowane przez organizację</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <ShareChoiceDialog shelter={shelter} variant="icon" />
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {shelter.city}{shelter.region && `, ${shelter.region}`}
                </p>
                {hasExactLocation(shelter) ? (
                  shelter.distance !== undefined && (
                    <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-medium text-primary bg-primary/10 px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                      <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                      {formatDistance(shelter.distance)}
                    </span>
                  )
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-medium text-muted-foreground bg-muted px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                    <MapPinOff className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    <span className="hidden xs:inline">Brak lokalizacji</span>
                  </span>
                )}
              </div>
              
              {/* Contact icons */}
              {(shelter.phone || shelter.email) && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                  {shelter.phone && (
                    <a 
                      href={`tel:${shelter.phone.split(';')[0].trim()}`}
                      onClick={(e) => { e.stopPropagation(); trackEvent("Telefon", { shelter_id: shelter.id, shelter_name: shelter.name }); }}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Phone className="w-3 h-3 flex-shrink-0" />
                      <span className="sm:hidden">{shelter.phone.split(';')[0].trim()}</span>
                      <span className="hidden sm:inline">{shelter.phone}</span>
                    </a>
                  )}
                  {shelter.email && (
                    <a 
                      href={`mailto:${shelter.email}`}
                      onClick={(e) => { e.stopPropagation(); trackEvent("Email", { shelter_id: shelter.id, shelter_name: shelter.name }); }}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Mail className="w-3 h-3 flex-shrink-0" />
                      <span className="break-all">{shelter.email}</span>
                    </a>
                  )}
                </div>
              )}
              
              {/* Unverified data badge with tooltip - hidden on mobile */}
              {!shelter.data_verified && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="hidden sm:block text-xs text-muted-foreground/60 mt-1 italic cursor-help hover:text-primary transition-colors">
                        Dane niezweryfikowane – pomóż je zaktualizować!
                      </p>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[250px] text-center">
                      <p>Informacje pochodzą z internetu i nie zostały jeszcze potwierdzone. Masz aktualne dane? Kliknij w kartę i zgłoś aktualizację!</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              
              <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-2 sm:mt-3">
                {isUrgentNeedsActive(shelter) && (
                  <span className="inline-flex items-center gap-1 badge-urgent text-[10px] sm:text-xs px-1.5 sm:px-2">
                    <AlertTriangle className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    Pilne
                  </span>
                )}
                {shelter.dog_walking_available === true && (
                  <span className="inline-flex items-center gap-1 badge-walking text-[10px] sm:text-xs px-1.5 sm:px-2">
                    <Heart className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    <span className="hidden xs:inline">Spacery</span>
                  </span>
                )}
                {shelter.volunteering_available === true && (
                  <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">
                    <Users className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    <span className="hidden xs:inline">Wolontariat</span>
                  </span>
                )}
                {shelter.dogs_available === true && (
                  <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                    <Dog className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    <span className="hidden xs:inline">Psy</span>
                  </span>
                )}
                {shelter.cats_available === true && (
                  <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                    <Cat className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                    <span className="hidden xs:inline">Koty</span>
                  </span>
                )}
              </div>
              
              {shelter.current_needs && (
                <p className="text-xs sm:text-sm text-muted-foreground mt-2 sm:mt-3 line-clamp-2">
                  <span className="font-medium text-foreground/80">Potrzeby:</span>{" "}
                  {shelter.current_needs}
                </p>
              )}

              {/* Last updated info - compact on mobile */}
              {shelter.last_updated && (
                <p className="text-xs text-muted-foreground/70 mt-1.5 sm:mt-2 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  <span className="sm:hidden">{getLastUpdatedText(shelter.last_updated)}</span>
                  <span className="hidden sm:inline">Aktualizacja {getLastUpdatedText(shelter.last_updated)}</span>
                </p>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Pagination - on mobile and tablet */}
      {isTabletOrMobile && totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            Poprzednia
          </Button>
          
          <span className="text-sm text-muted-foreground">
            Strona {currentPage} z {totalPages}
          </span>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="gap-1"
          >
            Następna
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Scroll to top - only on desktop when there are many shelters */}
      {!isTabletOrMobile && shelters.length > 4 && (
        <div className="pt-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={scrollToTop}
            className="w-full gap-2"
          >
            <ArrowUp className="w-4 h-4" />
            Przewiń do góry
          </Button>
        </div>
      )}
    </div>
  );
};
